import { defineStore } from 'pinia';

import apiService from '@/services/api.service';
import { Config } from '@/config';
import type { LocalizableString } from '@/services/types/api.types';

export interface StageCounts {
  prelaunch: number;
  ongoing: number;
  completed: number;
  archived: number;
}

export interface CustomerOverviewRow {
  id: number;
  slug: string;
  organizationName: LocalizableString | null;
  title: LocalizableString | null;
  status: number;
  accountType: number;
  commercialStatus: 'active' | 'churned' | 'unknown';
  customUrl: { enabled?: boolean; hostname?: string } | null;
  options: Record<string, any> | null;
  hubspotCompanyId: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  hsContractStart: string | null;
  hsContractEnd: string | null;
  hsSyncedAt: string | null;
  contractConfirmedBy: string | null;
  contractConfirmedAt: string | null;
  contractConfidence: 'unverified' | 'hubspot_matched' | 'confirmed';
  hsDiverged: boolean;
  createdAt: string;
  adminCount: number;
  editorCount: number;
  consultationsByStage: StageCounts;
  projectsByStage: StageCounts;
  consultationsCreatedInPeriod: number;
  projectsCreatedInPeriod: number;
  consultationsLaunchedInPeriod: number;
  projectsLaunchedInPeriod: number;
  lastActivityAt: string | null;
}

export interface DateRange {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
}

interface OverviewResponse {
  range: DateRange;
  // Whether the *current* user may finance-confirm contract dates. Computed by the API so
  // the finance identity itself never reaches the client.
  canConfirmContract: boolean;
  customers: CustomerOverviewRow[];
}

interface State {
  customers: CustomerOverviewRow[];
  range: DateRange | null;
  canConfirmContract: boolean;
  isLoading: boolean;
}

export const useCustomersOverviewStore = defineStore({
  id: 'customersOverview',
  state: (): State => ({
    customers: [],
    range: null,
    canConfirmContract: false,
    isLoading: false,
  }),
  actions: {
    async fetchCustomersOverview(range?: DateRange) {
      this.isLoading = true;
      try {
        const response = await apiService.get<OverviewResponse>(
          `${Config.api.base}/admin/customers-overview`,
          range ? { from: range.from, to: range.to } : undefined,
        );
        if (response.success) {
          this.customers = response.data.customers;
          this.range = response.data.range;
          this.canConfirmContract = response.data.canConfirmContract ?? false;
        }
      } finally {
        this.isLoading = false;
      }
    },

    async updateCommercial(
      id: number,
      payload: {
        accountType?: number;
        hubspotCompanyId?: string | null;
        contractStartDate?: string | null;
        contractEndDate?: string | null;
      },
    ) {
      const response = await apiService.patch<{
        id: number;
        accountType: number;
        hubspotCompanyId: string | null;
        contractStartDate: string | null;
        contractEndDate: string | null;
        commercialStatus: 'active' | 'churned' | 'unknown';
        hsContractStart: string | null;
        hsContractEnd: string | null;
        hsSyncedAt: string | null;
        contractConfirmedBy: string | null;
        contractConfirmedAt: string | null;
        contractConfidence: 'unverified' | 'hubspot_matched' | 'confirmed';
        hsDiverged: boolean;
      }>(`${Config.api.base}/admin/community/${id}/commercial`, payload);
      if (response.success) {
        const row = this.customers.find((c) => c.id === id);
        // Every response key is a CustomerOverviewRow key, so patch the row wholesale.
        if (row) Object.assign(row, response.data);
      }
      return response.data;
    },

    async pullHubspotContract(id: number) {
      const response = await apiService.post<{
        found: boolean;
        hubspotCompanyId: string | null;
        contractStartDate?: string | null;
        contractEndDate?: string | null;
        dealName?: string | null;
        annualAmount?: string | null;
      }>(`${Config.api.base}/admin/community/${id}/:hubspot-pull`);
      return response.data;
    },

    async confirmContract(id: number, confirmed: boolean) {
      const response = await apiService.post<{
        id: number;
        contractConfirmedBy: string | null;
        contractConfirmedAt: string | null;
        contractConfidence: 'unverified' | 'hubspot_matched' | 'confirmed';
        hsDiverged: boolean;
      }>(`${Config.api.base}/admin/community/${id}/:confirm-contract`, { confirmed });
      if (response.success) {
        const row = this.customers.find((c) => c.id === id);
        if (row) Object.assign(row, response.data);
      }
      return response.data;
    },
  },
});
